import { useState, type ReactNode } from 'react';
import {
  alternatives, calc, competing, conflicts, dateOf, extraGuestSuggestion, fDT, fShort, fTime, inr, span, timeOf, toLocal,
  type BookingRecord, type Conflict, type PricingPatch,
} from '@nandanam/domain';
import { useStore, type ActResult, type AppStore } from '../app/store';
import { Icon } from '../ui/icons';
import { Chips, MoneyInput } from '../ui/components';
import { PHASE_LABEL } from '../app/selectors';

/** Busy / error handling for a save button. Errors stay on screen until the next attempt. */
export function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<ActResult | null>): Promise<ActResult | null> => {
    setBusy(true); setError(null);
    try {
      const r = await fn();
      if (r && !r.ok) setError(r.error ?? 'Not saved');
      return r;
    } finally { setBusy(false); }
  };
  return { busy, error, setError, run };
}

export function ErrorBox({ error }: { error: string | null }) {
  if (!error) return null;
  return <div className="error-box" role="alert"><b>Not saved.</b> {error}</div>;
}

export function ViewOnlyNote() {
  const S = useStore();
  if (S.canEdit) return null;
  return <div className="info-box"><Icon name="eye" size={14} /> View only — you can look, but changes are made by the owner.</div>;
}

export function ModeChips({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const S = useStore();
  return <Chips value={value} options={S.state.settings.payModes.map(m => ({ v: m, label: m }))} onChange={onChange} />;
}

export function Figs({ items }: { items: [string, ReactNode][] }) {
  return (
    <div className="kpis" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))` }}>
      {items.map(([k, v]) => <div key={k} className="mini-fig"><div className="muted">{k}</div><b className="num">{v}</b></div>)}
    </div>
  );
}

export interface SuccessAction { label: string; go: () => void; primary?: boolean }
export function Success({ title, body, checked, actions }: { title: string; body?: ReactNode; checked?: ReactNode; actions: SuccessAction[] }) {
  return (
    <div className="success">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className="success-ring"><Icon name="check" size={18} /></span>
        <h3 style={{ fontSize: 22 }}>{title}</h3>
      </div>
      {body && <div className="muted" style={{ fontSize: 14 }}>{body}</div>}
      {checked}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
        {actions.map(a => <button key={a.label} className={`btn ${a.primary ? 'primary' : ''}`} onClick={a.go}>{a.label}</button>)}
      </div>
    </div>
  );
}

/** What the person can rely on after a hold or confirmation (see ARCHITECTURE §3, "honest limitation"). */
export function CheckedLine({ res }: { res: ActResult | null }) {
  const S = useStore();
  if (!res) return null;
  const x = res.opId ? S.saves.get(res.opId) : undefined;
  if (x?.state === 'failed') return <div className="error-box" role="alert"><b>Not saved.</b> {x.error}</div>;
  if (x?.state === 'uploading') return <div className="info-box"><Icon name="refresh" size={14} /> Saved on this device. Uploading to OneDrive and checking for changes from other devices… You can close this.</div>;
  if (x?.state === 'device') return <div className="warn-box"><Icon name="cloudOff" size={14} /> Saved on this device only — not in OneDrive yet. It uploads when you are back online.</div>;
  if (S.mode === 'demo') return <div className="info-box">Demo mode — checked against the sample data in this browser only.</div>;
  return x?.state === 'saved' || res.verified
    ? <div className="good-box"><Icon name="check" size={14} /> Saved to OneDrive and checked at {fTime(toLocal(x?.at ?? S.now))}. No clashing change from another device was found.</div>
    : <div className="warn-box"><Icon name="clock" size={14} /> Saved, but not yet checked against changes from other devices. The app re-checks on the next sync.</div>;
}

/** Booking chooser for overlays opened from Today or the sidebar without a record. */
export function RecordPick({ value, onChange, filter, placeholder = 'Choose a booking…' }: { value: string; onChange: (id: string) => void; filter: (r: BookingRecord) => boolean; placeholder?: string }) {
  const S = useStore();
  const list = S.state.records.filter(filter).sort((a, b) => span(a).start.localeCompare(span(b).start));
  return (
    <label className="field"><span className="lbl">Booking</span>
      <select className="select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {list.map(r => { const sp = span(r); return <option key={r.id} value={r.id}>{S.num(r)} · {S.cust(r.cust).name} · {fShort(sp.start, sp.end)}</option>; })}
      </select>
      {!list.length && <span className="muted" style={{ fontSize: 12 }}>No bookings need this right now.</span>}
    </label>
  );
}

export function conflictText(S: AppStore, c: Conflict): string {
  if (c.kind === 'block') return `${c.type} block · ${fShort(c.start, c.end)}`;
  const r = S.rec(c.id);
  return `${r ? S.cust(r.cust).name + ' · ' : ''}${PHASE_LABEL[c.phase as keyof typeof PHASE_LABEL] ?? c.phase} · ${fShort(c.start, c.end)}`;
}

/** Live availability for a record's own dates, excluding itself. */
export function availabilityFor(S: AppStore, r: BookingRecord) {
  const sp = span(r);
  const opts = { now: S.now, bufferHours: r.terms.bufferHours, excludeId: r.id };
  const past = sp.start <= toLocal(S.now);
  const cs = conflicts(S.state, sp.start, sp.end, opts);
  const comp = competing(S.state, sp.start, sp.end, r.id);
  const alts = cs.length ? alternatives(S.state, sp.start, sp.end, { ...opts, n: 3 }) : [];
  const ok = !past && !cs.length;
  const box = past
    ? <div className="error-box"><b>These dates are in the past.</b> Change the dates on a new enquiry instead.</div>
    : cs.length
      ? <div className="error-box"><b>Not available.</b> Clashes with {cs.map(c => conflictText(S, c)).join('; ')}.{alts.length ? <> Nearest free: {alts.map(a => fShort(a.start, a.end)).join(' · ')}.</> : null} {r.terms.bufferHours ? `Includes the ${r.terms.bufferHours} h gap between bookings.` : ''}</div>
      : <div className="good-box"><Icon name="check" size={14} /> Available · {fShort(sp.start, sp.end)}{comp.length ? ` · ${comp.length} other enquir${comp.length > 1 ? 'ies' : 'y'} for these dates — first to hold wins` : ''}</div>;
  return { ok, past, box };
}

/* ---------------- pricing editor (Adjust pricing, Confirm booking) ---------------- */

export interface PricingDraft {
  agreed: (number | '')[];
  extraOn: boolean; extraAmt: number | ''; extraNote: string;
  cleaning: number | '';
  discReason: string; discAmt: number | '';
}

export function pricingDraft(r: BookingRecord): PricingDraft {
  return {
    agreed: r.items.map(i => (i.custom && !i.agreed ? '' : i.agreed)), extraOn: !!r.extra, extraAmt: r.extra?.amount ?? '', extraNote: r.extra?.note ?? '',
    cleaning: r.cleaning, discReason: r.discount?.reason ?? '', discAmt: r.discount?.amount || '',
  };
}

export function patchFrom(r: BookingRecord, d: PricingDraft, S: AppStore): PricingPatch {
  const over = Math.max(0, r.guests - Math.max(...r.items.map(i => S.pkg(i.pkg)?.capacity ?? 0)));
  return {
    agreed: d.agreed.map(a => Number(a || 0)),
    extra: d.extraOn && Number(d.extraAmt) > 0 ? { guests: r.extra?.guests ?? over, amount: Number(d.extraAmt), note: d.extraNote } : null,
    cleaning: Number(d.cleaning || 0),
    discount: Number(d.discAmt) > 0 ? { amount: Number(d.discAmt), reason: d.discReason.trim() } : null,
  };
}

export function previewWith(r: BookingRecord, p: PricingPatch): BookingRecord {
  return { ...r, items: r.items.map((i, n) => ({ ...i, agreed: p.agreed?.[n] ?? i.agreed })), extra: p.extra ?? null, cleaning: p.cleaning ?? r.cleaning, discount: p.discount ?? null };
}

export function pricingProblem(r: BookingRecord, d: PricingDraft): string | null {
  if (d.agreed.some(a => a === '')) return 'Enter an agreed price for each package';
  if (r.items.some((i, n) => i.custom && !(Number(d.agreed[n]) > 0))) return 'Enter the price agreed for the small event';
  if (Number(d.discAmt) > 0 && !d.discReason.trim()) return 'Give a reason for the discount';
  return null;
}

export function PricingEditor({ r, d, set }: { r: BookingRecord; d: PricingDraft; set: (d: PricingDraft) => void }) {
  const S = useStore(), s = S.state.settings;
  const p = patchFrom(r, d, S), k = calc(previewWith(r, p)), std = calc(r).std + calc(r).clean;
  const firstPkg = S.pkg(r.items[0]!.pkg);
  const sug = firstPkg ? extraGuestSuggestion(firstPkg, r.guests, r.items[0]!.start, r.items[0]!.end, s.extraGuestRate) : { over: 0, amount: 0 };
  const diff = k.total - k.damage - std - k.custom;
  const row = (label: ReactNode, hint: ReactNode, input: ReactNode) => (
    <div className="price-row"><div style={{ minWidth: 0 }}>{label}{hint && <div className="muted" style={{ fontSize: 12 }}>{hint}</div>}</div>{input}</div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {r.items.map((it, n) => row(<span>{it.pkgName}</span>, <>{fShort(it.start, it.end)} · {it.custom ? 'No fixed price — enter the agreed price' : `Std ${inr(it.std)}`}</>,
        <MoneyInput value={d.agreed[n] ?? ''} onChange={v => set({ ...d, agreed: d.agreed.map((a, m) => (m === n ? v : a)) })} />))}
      {row(<label className="toggle"><input type="checkbox" checked={d.extraOn} onChange={e => set({ ...d, extraOn: e.target.checked, extraAmt: e.target.checked && d.extraAmt === '' ? sug.amount : d.extraAmt })} />Extra guests</label>,
        sug.over ? `${sug.over} over standard capacity · suggested ${inr(sug.amount)}` : 'Within standard capacity',
        <MoneyInput value={d.extraOn ? d.extraAmt : ''} disabled={!d.extraOn} onChange={v => set({ ...d, extraAmt: v })} />)}
      {row(<span>Cleaning</span>, <>Std {inr(calc(r).clean)}</>, <MoneyInput value={d.cleaning} onChange={v => set({ ...d, cleaning: v })} />)}
      <div className="price-row">
        <input className="input" placeholder="Discount reason" value={d.discReason} onChange={e => set({ ...d, discReason: e.target.value })} />
        <MoneyInput value={d.discAmt} onChange={v => set({ ...d, discAmt: v })} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: 10, borderTop: '1px solid var(--line-3)' }}>
        <span style={{ fontFamily: 'var(--font-heading)', fontSize: 18 }}>Booking total</span>
        <span className="num" style={{ fontFamily: 'var(--font-heading)', fontSize: 26 }}>{inr(k.total)}</span>
      </div>
      <div className="muted" style={{ fontSize: 12.5, textAlign: 'right' }}>{diff ? `${diff > 0 ? 'Above' : 'Below'} standard ${inr(std)} by ${inr(Math.abs(diff))}` : k.custom ? 'Small-event price as agreed' : 'At the standard rate'}</div>
    </div>
  );
}

export const todayISO = (S: AppStore) => dateOf(S.now);
export const timeNow = (S: AppStore) => timeOf(toLocal(S.now));
export { fDT };
